import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import PopOver from '../../components/deprecated/PopOver';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { deltaToText } from '../../utils/text/deltaText';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import { useCanShowWideMagneticWallItems } from '../PostLayout';
import CommentThreadCompact from './CommentThreadCompact';
import type { CommentThreadFullProps } from './CommentThreadFull';
import CommentThreadFull from './CommentThreadFull';
import type { CompactPopOverSide } from './getMagneticWallItemLayout';
import { isClickingMagneticWallItemOverflowButton } from './MagneticWallItemOverflowMenu';
import type { ExtractCommentThreadTypes } from '../../utils/post/threadUtils';

const POST_FRAGMENT = gql(`
  fragment CommentThread_post on RootPostFields {
    ...CommentThreadFull_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThread_thread on RootThreadFields {
    id
    ... on CommentThread {
      comments {
        id
        content
      }
    }
    ... on PublicCommentThread {
      comments {
        id
        content
      }
    }
    ...CommentThreadFull_thread
    ...CommentThreadCompact_thread
  }
`);

const AVG_CHARS_PER_LINE = 35;
const MAX_LINES = 8;

const isThreadWide = (
  thread: ExtractCommentThreadTypes<typeof THREAD_FRAGMENT>,
) => {
  return thread.comments.some((comment) => {
    const text = deltaToText(comment.content) || '';
    const lines = text.split(/\n+/g);
    const numLines = lines.reduce(
      (acc, line) => acc + Math.ceil(line.length / AVG_CHARS_PER_LINE),
      0,
    );
    return numLines > MAX_LINES;
  });
};

type CommentThreadProps = {
  thread: ExtractCommentThreadTypes<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isFocused?: boolean;
  minimized?: boolean;
  isPopOverOpen?: boolean;
  initialShowingMore?: boolean;
  compactPopOverSide: CompactPopOverSide;
  overlapped: boolean;
  onResize: () => void;
  size: MagneticWallItemSize;
  onPopOverHeightChange: (height: number) => void;
} & Pick<
  CommentThreadFullProps,
  | 'unsavedInputValue'
  | 'onInputChange'
  | 'unresolveThread'
  | 'createComment'
  | 'deleteThread'
  | 'deleteComment'
  | 'addComment'
  | 'toggleCommentReaction'
>;

const CommentThread = ({
  thread,
  post,
  isFocused = false,
  isPopOverOpen = false,
  initialShowingMore = false,
  compactPopOverSide,
  overlapped,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
  createComment,
  addComment,
  deleteThread,
  deleteComment,
  toggleCommentReaction,
  size,
  onPopOverHeightChange,
}: CommentThreadProps) => {
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const canShowWideComments = useCanShowWideMagneticWallItems();

  const isWide =
    useMemo(() => isThreadWide(thread), [thread]) && canShowWideComments;

  useEffect(() => {
    onResize?.();
  }, [onResize]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightComment(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  const handleClick = (e: React.MouseEvent) => {
    if (
      !isPopOverOpen &&
      !isClickingMagneticWallItemOverflowButton(e.nativeEvent)
    ) {
      dispatch(
        EditorActions.focusMagneticWall({
          id: thread.id,
          type: 'comment',
          isActive: true,
          hasExpandIntent: true,
        }),
      );
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
    setIsHovered(false);
  };

  const fullViewProps = {
    onResize,
    thread,
    unsavedInputValue,
    initialShowingMore,
    onInputChange,
    createComment,
    addComment,
    deleteThread,
    deleteComment,
    toggleCommentReaction,
    post,
  };

  return (
    <MagneticWallItem.Root
      id={`thread-${thread.id}`}
      focused={isFocused}
      wide={isWide}
      fullView={
        <MagneticWallItem.Card
          focused={isFocused}
          focusable
          overlapped={overlapped}
        >
          <CommentThreadFull
            {...fullViewProps}
            isExpanded={isFocused}
            hovered={isHovered}
            unresolveThread={unresolveThread}
            wide={isWide}
          />
        </MagneticWallItem.Card>
      }
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={handleBlur}
          target={<CommentThreadCompact thread={thread} />}
          onPopOverHeightChange={onPopOverHeightChange}
          content={
            <PopOver.Container noPadding width="auto" fullHeight>
              {(popoverProps: { scrollToBottom: () => void }) => (
                <CommentThreadFull
                  {...fullViewProps}
                  isExpanded={isFocused}
                  hovered={isHovered}
                  onCreated={() => {
                    setTimeout(popoverProps.scrollToBottom, 200);
                  }}
                  unresolveThread={unresolveThread}
                />
              )}
            </PopOver.Container>
          }
          compactPopOverSide={compactPopOverSide}
          open={isPopOverOpen}
        />
      }
      size={size}
    />
  );
};

export default CommentThread;
